
import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { useNavigate } from "react-router";

import { Grid, Button, IconButton, Switch, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography, Dialog, DialogActions, DialogContent, TextField, Box, FormHelperText, List, ListItem, ListItemText, CircularProgress, Paper } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';

import { TablePaginationActions } from "../../helpers/TablePaginationActions";

import { usePages } from "../../hooks/usePages";
import { useFilter } from "../../hooks/useFilter";

import { openSnackbar } from "../../store/slices/SnackbarSlice";

import { TableTheadStyle, TablePaddingStyle } from "../../styles/TableStyle";
import { DialogActionsStyle, DialogContentStyle } from "../../styles/DialogStyle";

import { Layout } from "../../components/Layout"
import { SearchTextField } from "../../components/SearchTextField";

import { ActualizarApiKey, ActualizarApiKeyEstatus, AgregarApiKey, ObtenerApiKeys } from "../../connections/admin/ApiKeysConnection";

import { ApiKeyDominioProps, ApiKeyProps } from "../../interfaces/admin/ApiKeysInterface";

export const ApiKeysPage = () => {

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const [openDialog, setOpenDialog] = useState( false );

    const [loading, setLoading] = useState( false );
    const [loadingData, setLoadingData] = useState( true );

    const [rows, setRows] = useState<ApiKeyProps[]>( [] );

    const [descripcion, setDescripcion] = useState<string>( '' );
    const [dominio, setDominio] = useState<string>( '' );
    const [dominios, setDominios] = useState<ApiKeyDominioProps[]>( [] );

    const [referencia, setReferencia] = useState<string>( '' );

    const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, resetPage } = usePages( 5 );

    const { filtro, setFiltro, dataFiltrada } = useFilter( rows, ( item, filtro ) => item.descripcion?.toLowerCase().includes( filtro.toLowerCase() ) ?? false );

    const handleClose = () => {

        setReferencia( '' );
        setDescripcion( '' );
        setDominio( '' );
        setDominios( [] );

        setOpenDialog( false );
    }

    const handleChangeApiKeyActiva = async ( value: boolean, id: string ) => {

        await ActualizarApiKeyEstatus({ id, activar: value }).then( resp => {

            const { success, message } = resp;

            if ( success ) {

                const newArray = rows.map( ( elem ) => {
                    if ( elem.id === id ) {
                        elem.activo = value;
                    }
                    return elem;
                });

                setRows( newArray );

                dispatch( openSnackbar({ message: message, variant: 'success' }));

            }
            else {
                dispatch( openSnackbar({ message: message, variant: 'error' }));
            }

        })

    }

    const handleEdit = ( descripcion: string, dominios: ApiKeyDominioProps[], referencia: string ) => {

        setDescripcion( descripcion );
        setDominios( dominios );
        setReferencia( referencia );

        setOpenDialog( true );
    }

    const handleAgregarDominio = () => {

        const validateDominio = dominios.find( ( element ) => { return element.dominio === dominio });

        if ( dominio.includes('http') || dominio.includes('https') || dominio.includes(':') || dominio.includes('/') ) {
            dispatch( openSnackbar({ message: 'El dominio no debe incluir http o https y ningun caracter como :, /', variant: 'warning' }));
        }
        else if ( !validateDominio && dominio !== '' ) {

            const id = new Date().getUTCSeconds() + '' + new Date().getUTCMilliseconds();

            setDominios([
                ...dominios,
                {
                    id,
                    dominio,
                    activo: true,
                }
            ]);

            setDominio( '' );
        }
        else {
            dispatch( openSnackbar({ message: dominio === '' ? 'Escribe un dominio' : 'Ya se tiene agregado un dominio con la misma descripción', variant: 'warning' }));
        }

    }

    const handleDeleteDomain = ( id: string ) => {

        const newDomains = dominios.filter( ( elem ) => elem.id !== id );
        setDominios( newDomains );
    }

    const handleCopy = ( apiKey: string ) => {

        navigator.clipboard.writeText( apiKey ?? '' );
        dispatch( openSnackbar({ message: 'Se copio la  api key correctamente', variant: 'success' }));

    }

    const handleGuardarApiKey = async () => {

        setLoading( true );

        if ( referencia !== '' ) {

            await ActualizarApiKey({ id: referencia, descripcion, dominios }).then( resp => {

                setTimeout(() => {

                    const { success, message, descripcion, dominios } = resp;

                    if ( success ) {

                        setRows(rows.map( ( elem ) => {

                            if ( elem.id === referencia ) {
                                elem.descripcion = descripcion;
                                elem.dominios = dominios;
                            }
                            return elem;
                        }));

                        handleClose();

                        setLoading( false );

                        dispatch( openSnackbar({ message: message, variant: 'success' }));

                    }
                    else {
                        dispatch( openSnackbar({ message: message, variant: 'error' }));
                    }

                }, 700);

            })

        }
        else if ( referencia === '' ) {

            await AgregarApiKey({ descripcion, dominios }).then( resp => {

                setTimeout(() => {

                    const { success, message, id, idApiKeys, descripcion, fechaCreacion, apiKey, dominios, activo } = resp;

                    if ( success ) {

                        setRows([
                            ...rows,
                            {
                                descripcion: descripcion,
                                fechaCreacion: fechaCreacion,
                                apiKey: apiKey,
                                id: id,
                                idApiKeys: idApiKeys,
                                dominios: dominios,
                                activo: activo,
                            }
                        ]);

                        handleClose();

                        setLoading( false );

                        dispatch( openSnackbar({ message: message, variant: 'success' }));

                    }
                    else {
                        dispatch( openSnackbar({ message: message, variant: 'error' }));
                    }

                }, 700);

            })

        }

    }

    useEffect(() => {

        async function obtener() {

            setLoadingData( true );
            setRows( [] );

            await ObtenerApiKeys().then( resp => {

                setTimeout(() => {

                    setRows( resp );
                    setLoadingData( false );

                }, 400);

            });

        }

        obtener();

    }, [])

    return (

        <Layout breadcrumbs={ [{ text: 'Administración' }, { text: 'Api Key´s' }] } backgroundPaper={ false }>

            <Grid container spacing={ 3 }>

                <Grid size={{ xs: 6 }} textAlign={ 'start' }>

                    <Button startIcon={ <UndoIcon /> } color="secondary" variant="text" sx={{ mt: 1 }} onClick={ () => navigate( '/' ) }>
                        Regresar
                    </Button>

                </Grid>

                <Grid size={{ xs: 6 }} textAlign={ 'end' }>

                    <Button variant="contained" sx={{ mt: 1 }} onClick={ () => setOpenDialog( true ) }>
                        Agregar
                    </Button>

                </Grid>

            </Grid>

            <Grid container sx={{ mt: 2 }}>
                <Grid size={{ xs: 12 }}>
                    <SearchTextField
                        value={ filtro }
                        label="Buscar por descripción"
                        onChange={ ( value ) => {
                            setFiltro( value );
                            resetPage();
                        }}
                        onClear={ () => resetPage() }
                    />
                </Grid>
            </Grid>

            <TableContainer sx={{ mt: 3, borderRadius: 3 }} component={ Paper }>

                <Table sx={{ minWidth: 500 }}>

                    <TableHead sx={{ ...TableTheadStyle }}>

                        <TableRow sx={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>

                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width:'5%' }}> Id </TableCell>
                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width:'15%' }}> Descripción </TableCell>
                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width:'35%' }}> Api Key </TableCell>
                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width:'30%' }}> Dominio(s) Permitido(s) </TableCell>
                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width:'10%' }}> </TableCell>

                        </TableRow>

                    </TableHead>

                    <TableBody>

                        {
                            (
                                rowsPerPage > 0
                                ?
                                    dataFiltrada.slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage )
                                :
                                    dataFiltrada
                            )
                            .map( ( { id, idApiKeys, descripcion, apiKey, activo, dominios }, index ) => (

                                <TableRow key={ index }>

                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        { idApiKeys }
                                    </TableCell>

                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        { descripcion }
                                    </TableCell>

                                    <TableCell>
                                        <Grid container spacing={ 1 }>
                                            <Grid>
                                                <Button
                                                    sx={{ textTransform: 'none' }}
                                                    onClick={ () => handleCopy( apiKey ?? '' ) }
                                                    endIcon={ <ContentPasteIcon /> }
                                                >
                                                    { apiKey }
                                                </Button>
                                            </Grid>
                                            <Grid>

                                            </Grid>
                                        </Grid>
                                    </TableCell>

                                    <TableCell>

                                        {
                                            dominios?.map( ( { dominio }, i ) => (
                                                <Typography key={ i } variant='subtitle2' sx={{ my: 1 }}>{ ( i + 1 ) + '.- ' + dominio }</Typography>
                                            ))
                                        }

                                    </TableCell>

                                    <TableCell>

                                        <Switch
                                            checked={ activo }
                                            onChange={ (e) => handleChangeApiKeyActiva( e.target.checked, id ?? '' ) }
                                        />

                                        <IconButton
                                            color="primary"
                                            aria-label="edit"
                                            component="span"
                                            onClick={ () => handleEdit( descripcion ?? '', dominios ?? [], id ?? '' ) }
                                        >
                                            <EditIcon />
                                        </IconButton>

                                    </TableCell>

                                </TableRow>

                            ))
                        }

                        {
                            ( dataFiltrada.length === 0 && !loadingData )
                            ?
                                <TableRow style={{ height: 73 }}>

                                    <TableCell colSpan={ 7 } align="center">

                                        <Typography variant="caption" color='secondary'> No se encontraron resultados </Typography>

                                    </TableCell>

                                </TableRow>
                            :
                                loadingData
                                &&
                                    <TableRow style={{ height: 73 }}>

                                        <TableCell colSpan={ 7 } align="center">

                                            <CircularProgress size={ 32 } />

                                        </TableCell>

                                    </TableRow>
                        }

                    </TableBody>

                    <TableFooter>

                        {
                            dataFiltrada.length !== 0
                            &&
                                <TableRow>

                                    <TableCell colSpan={ 2 } sx={{ border: 'none'}}>
                                        <Typography variant='body2' color='black' sx={{ fontWeight: 'bold' }}>
                                            Total de registros: { dataFiltrada.length }
                                        </Typography>
                                    </TableCell>

                                    <TablePagination
                                        sx={{ border: 'none'}}
                                        rowsPerPageOptions={[ 5, 10, 15, 25, 50, 75, 100, 150, 200, 500 ]}
                                        colSpan={ 8 }
                                        count={ dataFiltrada.length }
                                        rowsPerPage={ rowsPerPage }
                                        page={ page }

                                        onPageChange={ handleChangePage }
                                        onRowsPerPageChange={ handleChangeRowsPerPage }

                                        ActionsComponent={ TablePaginationActions }

                                        labelRowsPerPage="Filas por página"
                                        labelDisplayedRows={
                                            ({ from, to, count }) => {
                                            return '' + from + '-' + to + ' de ' + count
                                            }
                                        }
                                    />

                                </TableRow>
                        }

                    </TableFooter>

                </Table>

            </TableContainer>

            <Dialog open={ openDialog } maxWidth='sm'>

                <DialogContent sx={ DialogContentStyle }>

                    <Grid container spacing={ 3 } mt={ 3 }>

                        <Grid size={{ xs: 12, md: 12 }}>

                            <TextField
                                name='descripcion'
                                label='Descripción'
                                variant='standard'
                                type='text'
                                autoComplete='off'
                                fullWidth
                                value={ descripcion }
                                onChange={ (e) => setDescripcion( e.target.value ) }
                                slotProps={{
                                    input: {
                                        autoComplete: 'off'
                                    },
                                    inputLabel: {
                                        shrink: true,
                                    }
                                }}
                            />

                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>

                            <Box component="div" sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>

                                <TextField
                                    label="Agregar dominio"
                                    name='dominiosAutorizados'
                                    variant="standard"
                                    autoComplete='off'
                                    fullWidth
                                    value={ dominio }
                                    onChange={ (e) => setDominio( e.target.value ) }
                                    slotProps={{
                                        input: {
                                            autoComplete: 'off'
                                        },
                                        inputLabel: {
                                            shrink: true,
                                        }
                                    }}
                                />

                                <Button
                                    variant='contained'
                                    sx={{ borderRadius: 10 }}
                                    size='small'
                                    onClick={ handleAgregarDominio }
                                >
                                    <AddIcon fontSize='small' />
                                </Button>

                            </Box>

                            <FormHelperText sx={{ mt: 1 }}>Permite a los sitios web especificados hacer solicitudes mediante el uso de la clave</FormHelperText>

                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>

                            <List subheader={ <Typography variant='h6' sx={{ fontSize: 16, mb: 2, fontWeight: 'bold' }} >Dominio(s) permitido(s)</Typography> }>

                                {
                                    dominios.map( ( { dominio, id }, index ) => (

                                        <ListItem disablePadding key={ index } sx={{ mb: 1 }}>

                                            <ListItemText
                                                primary={ dominio }
                                            />

                                            <IconButton color='primary' onClick={ () => handleDeleteDomain( id ) }>
                                                <DeleteIcon />
                                            </IconButton>

                                        </ListItem>

                                    ))
                                }

                            </List>

                        </Grid>

                    </Grid>

                </DialogContent>

                <DialogActions sx={ DialogActionsStyle }>
                    <Button disabled={ loading } onClick={ () => setOpenDialog( false ) }> Cancelar </Button>
                    <Button loading={ loading } disabled={ descripcion === '' } onClick={ handleGuardarApiKey } variant='contained'> Guardar </Button>
                </DialogActions>

            </Dialog>

        </Layout>

    )
}
